/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion, AnimatePresence } from 'motion/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Leaf, Award, FileText, MapPin, Coins } from 'lucide-react';
import { productsService } from '../api';
import { useAuth } from './AuthContext';
import { cloudinaryThumb } from '../utils/imageUrl';

const CAROUSEL_INTERVAL_MS = 4500;

export default function Hero({ onExplore, onBecomeSeller }) {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [latestProducts, setLatestProducts] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    productsService
      .getAll({ page: 1, limit: 5 })
      .then((res) => {
        if (cancelled) return;
        const items = (res?.data?.items || []).filter((p) => p.image);
        setLatestProducts(items);
      })
      .catch(() => { /* ignore — fallback image will render */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (latestProducts.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % latestProducts.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [latestProducts.length]);

  const featured = latestProducts[activeIndex];
  const openFeatured = () => {
    if (!featured) return;
    navigate(`/ponuda?oglas=${featured.slug || featured.id}`);
  };

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-100 text-sage-800 text-sm font-bold mb-6 border border-sage-200">
              <Leaf size={14} className="text-sage-600" />
              <span>Direktno sa domaćih farmi</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-earth-900 leading-[1.1] mb-6">
               Sveža hrana, <br />
               <span className="text-sage-600 italic">od njive do trpeze.</span>
            </h1>
            <p className="text-lg sm:text-xl text-sage-800/80 mb-8 max-w-lg leading-relaxed">
              Pronađite najkvalitetnije domaće proizvode direktno od proizvođača.
              Podržite lokalne poljoprivrednike i jedite zdravo.
            </p>
            <div className="flex flex-wrap gap-4">
              <motion.button
                whileHover={{ scale: 1.02, backgroundColor: '#064e3b' }}
                whileTap={{ scale: 0.98 }}
                onClick={onExplore}
                className="px-8 py-4 bg-sage-800 text-white rounded-2xl font-bold flex items-center gap-2 shadow-xl shadow-sage-900/20 transition-all"
              >
                Istraži ponudu
                <ArrowRight size={20} />
              </motion.button>
              {isLoggedIn ? (
                <motion.button
                  whileHover={{ scale: 1.02, borderColor: '#065f46' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/moji-oglasi')}
                  className="px-8 py-4 bg-white border-2 border-sage-100 text-sage-800 rounded-2xl font-bold hover:bg-sage-50 transition-all flex items-center gap-2"
                >
                  <FileText size={18} />
                  Pogledaj svoje oglase
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02, borderColor: '#065f46' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onBecomeSeller}
                  className="px-8 py-4 bg-white border-2 border-sage-100 text-sage-800 rounded-2xl font-bold hover:bg-sage-50 transition-all"
                >
                  Postani prodavac
                </motion.button>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 30 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Decorative back elements */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-sage-100 rounded-full blur-2xl opacity-60 animate-pulse" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-amber-100 rounded-full blur-2xl opacity-60" />

            <div
              onClick={openFeatured}
              className={`aspect-[4/5] relative rounded-[3rem] overflow-hidden shadow-[0_32px_64px_-16px_rgba(5,150,105,0.15)] border-8 border-white ${featured ? 'cursor-pointer group/card' : ''}`}
            >
              <AnimatePresence mode="wait" initial={false}>
                {featured ? (
                  <motion.img
                    key={featured.id || featured.image}
                    src={cloudinaryThumb(featured.image, 900)}
                    alt={featured.name}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-105"
                  />
                ) : (
                  <motion.img
                    key="fallback"
                    src="https://images.unsplash.com/photo-1466692476868-aef1dfb1b816?q=80&w=2000&auto=format&fit=crop"
                    alt="Sveže povrće"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </AnimatePresence>

              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 via-earth-900/15 to-transparent" />

              {featured && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={featured.id || featured.image}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="absolute bottom-0 left-0 right-0 p-8 text-white z-10"
                  >
                    <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-md px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-widest mb-3 border border-white/20">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage-300 animate-pulse" />
                      Novi oglas
                    </span>
                    <h3 className="text-3xl font-serif font-bold leading-tight mb-2 line-clamp-2 drop-shadow-md">
                      {featured.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 font-bold text-amber-300">
                        <Coins size={15} />
                        {featured.price}
                        <span className="text-xs text-white/80 font-medium">RSD / {featured.unit}</span>
                      </span>
                      {featured.location && (
                        <span className="flex items-center gap-1 text-white/80 text-xs">
                          <MapPin size={12} />
                          {featured.location}
                        </span>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              )}

              {latestProducts.length > 1 && (
                <div className="absolute bottom-3 right-6 z-10 flex items-center gap-1.5 bg-white/15 backdrop-blur-md px-2 py-1.5 rounded-full border border-white/20">
                  {latestProducts.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setActiveIndex(i); }}
                      aria-label={`Idi na oglas ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === activeIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Floating Badge - Right Top */}
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 0.5 }}
              className="absolute -top-5 right-2 sm:-top-8 sm:-right-8 bg-white p-3.5 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] shadow-2xl border border-sage-50 flex items-center gap-3 sm:gap-4 z-20"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600">
                <Award size={24} />
              </div>
              <div className="pr-2">
                <p className="text-xs sm:text-sm font-black text-earth-900 uppercase tracking-tight">100% Domaće</p>
                <p className="text-[11px] sm:text-xs font-medium text-sage-600">Provereni proizvođači</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Background shapes */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-sage-200/20 blur-[120px] rounded-full -translate-x-1/2" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-sage-600/10 blur-[150px] rounded-full translate-x-1/4" />
    </section>
  );
}
